import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { registerWidget } from '../widgetRegistry';
import { useLayoutCtx, useWidgetActions } from '../LayoutContext';
import { authHeaders, StatusBadge, PriorityBadge } from '../../CaseClient';

// Granular Home-surface widget — paginated cases grid that fetches its own
// data (independent of CaseClientShell's shell-level cases state). When
// `ctx.callbacks.onOpenCase` is wired (the default home mount provides it
// from CaseClientShell) clicking a row opens the case tab; otherwise the
// widget publishes `xv.OpenCase` for any other widget to consume.

const gridTableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'auto' };
const gridThStyle = {
  position: 'sticky', top: 0, background: 'var(--bg-secondary)', textAlign: 'left',
  padding: '8px 12px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase',
  letterSpacing: '0.05em', color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-color)', zIndex: 1,
};
const gridTdStyle = { padding: '7px 12px', verticalAlign: 'middle', borderBottom: '1px solid var(--border-color)' };
const inputStyle = { padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' };
const subBarStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 18px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, flexWrap: 'wrap' };
const btnSecondary = { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px' };

function HomeCasesGrid({ node, config }) {
  const { token, workspaceId, callbacks, bus } = useLayoutCtx();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const initialStatus = config.default_status_filter ?? 'open,in_progress,under_review';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const showSearch = config.show_search !== false;
  const showStatusFilter = config.show_status_filter !== false;
  const pageSize = Math.min(500, Math.max(10, config.page_size || 100));

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError('');
    try {
      const params = { page_size: pageSize };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases`,
        { headers: authHeaders(token), params },
      );
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setCases(items);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load cases.');
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [token, workspaceId, statusFilter, search, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Phase L4a — exposes a `refresh` action a wire can target.
  useWidgetActions(node?.id, { refresh: () => load() });

  const handleOpen = (c) => {
    if (callbacks && typeof callbacks.onOpenCase === 'function') {
      callbacks.onOpenCase(c);
    }
    if (bus && typeof bus.publish === 'function') {
      bus.publish('xv.OpenCase', { case: c });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {(showSearch || showStatusFilter) && (
        <div style={subBarStyle}>
          {showSearch && (
            <input
              type="text"
              placeholder="Search cases…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, flex: '1 1 200px', maxWidth: '320px' }}
            />
          )}
          {showStatusFilter && (
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
              <option value="open,in_progress,under_review">Active</option>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="under_review">Under review</option>
              <option value="resolved,closed">Resolved/Closed</option>
              <option value="">All</option>
            </select>
          )}
          <button onClick={load} style={btnSecondary} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
          <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '12px' }}>
            {cases.length} case{cases.length === 1 ? '' : 's'}
          </span>
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {error ? (
          <div style={{ padding: '14px', color: '#ef4444', fontSize: '13px' }}>{error}</div>
        ) : loading && cases.length === 0 ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
        ) : cases.length === 0 ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>No cases match the current view.</div>
        ) : (
          <table style={gridTableStyle}>
            <thead>
              <tr>
                <th style={gridThStyle}>Case #</th>
                <th style={gridThStyle}>Title</th>
                <th style={gridThStyle}>Status</th>
                <th style={gridThStyle}>Priority</th>
                <th style={gridThStyle}>Stage</th>
                <th style={gridThStyle}>Created</th>
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr key={c.id} onClick={() => handleOpen(c)} style={{ cursor: 'pointer' }}>
                  <td style={{ ...gridTdStyle, fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {c.case_number || (c.id ? c.id.slice(0, 8) : '')}
                  </td>
                  <td style={{ ...gridTdStyle, fontWeight: 500 }}>{c.title || '(untitled)'}</td>
                  <td style={gridTdStyle}><StatusBadge status={c.status} /></td>
                  <td style={gridTdStyle}><PriorityBadge priority={c.priority} /></td>
                  <td style={{ ...gridTdStyle, fontSize: '12px', color: 'var(--text-secondary)' }}>{c.current_stage_name || '—'}</td>
                  <td style={{ ...gridTdStyle, fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

registerWidget({
  type: 'home.cases_grid',
  categories: ['home'],
  palette_label: 'Cases grid',
  description: 'Paginated cases data grid with optional search + status filter. Click a row to open the case tab.',
  Component: HomeCasesGrid,
  configSchema: {
    fields: [
      { type: 'boolean', name: 'show_search', label: 'Show search box', default: true },
      { type: 'boolean', name: 'show_status_filter', label: 'Show status filter', default: true },
      { type: 'text', name: 'default_status_filter', label: 'Default status filter (comma-separated)', default: 'open,in_progress,under_review' },
      { type: 'number', name: 'page_size', label: 'Page size', default: 100, min: 10, max: 500 },
    ],
  },
  publishes: ['xv.OpenCase'],
  subscribes: ['xv.RefreshContext'],
  actions: [
    { name: 'refresh', label: 'Reload cases', description: 'Re-fetches the cases list with the current filters.' },
  ],
  group: 'Home',
});
